<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filter.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filter.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 160px; max-width: 220px;">
              <label class="filter-label">Canal</label>
              <b-form-select size="sm" v-model="filter.channel" :options="arr.select.channels"></b-form-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 180px; max-width: 260px;">
              <label class="filter-label">Módulo</label>
              <b-form-select size="sm" v-model="filter.module_id" :options="moduleOptions"></b-form-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 140px; max-width: 180px;">
              <label class="filter-label">Estado</label>
              <b-form-select size="sm" v-model="filter.status" :options="arr.select.status"></b-form-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="search()" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>

        <b-alert variant="info" :show="!!filter.reference" class="notifications-log-reference-alert mb-0">
          <span>Mostrando solo las notificaciones de <strong>{{ filter.reference }}</strong></span>
          <b-button size="sm" variant="outline-info" @click="clearReferenceFilter()">Quitar filtro</b-button>
        </b-alert>
      </b-card>

      <b-card>
        <b-table class="app-table"
                  responsive
                  :stacked="isMobileViewport ? 'md' : false"
                  sticky-header="60vh"
                  :hover="true"
                  :fixed="true"
                  :items="table.items"
                  :fields="table.fields"
                  :current-page="table.currentPage"
                  :per-page="table.perPage"
                  :tbody-tr-class="rowClass"
                  @row-clicked="openSub"
                  v-if="table.items.length">

              <template v-slot:table-colgroup="scope">
                <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.width }">
              </template>

              <template v-slot:cell(date_time)="data">
                <span class="notifications-log-date" v-b-tooltip.hover.bottom :title="formatDate(data.item.date_time)">
                  <AppIcon name="schedule" class="mr-1" />{{ capitalize(formatRelative(data.item.date_time)) }}
                </span>
              </template>

              <template v-slot:cell(channel)="data">
                <b-badge :variant="channelVariant(data.item.channel)">
                  <AppIcon :name="channelIcon(data.item.channel)" class="mr-1" />{{ channelLabel(data.item.channel) }}
                </b-badge>
              </template>

              <template v-slot:cell(process)="data">
                <div class="notifications-log-process">
                  <strong v-if="data.item.module">{{ moduleLabel(data.item.module) }}</strong>
                  <span class="text-muted" v-else>Sistema</span>
                  <span class="notifications-log-process__reference" v-if="data.item.reference">{{ data.item.reference }}</span>
                </div>
              </template>

              <template v-slot:cell(to)="data">
                <div class="notifications-log-recipient" v-if="data.item.to">
                  <AppIcon name="person" class="mr-1" />
                  <span>{{ data.item.to }}</span>
                </div>
                <span class="text-muted" v-else>Sin destinatario</span>
              </template>

              <template v-slot:cell(description)="data">
                <div class="notifications-log-message" v-if="data.item.description">
                  <strong class="notifications-log-message__title" v-if="messageTitle(data.item)">{{ messageTitle(data.item) }}</strong>
                  <span>{{ messageDetail(data.item) }}</span>
                </div>
                <span class="text-muted" v-else>Sin contenido</span>
              </template>

              <template v-slot:cell(status)="data">
                <b-badge variant="success" v-if="data.item.status" v-b-tooltip.hover title="Enviado correctamente">
                  <AppIcon name="task_alt" class="mr-1" />Enviado
                </b-badge>
                <b-badge variant="danger" v-else v-b-tooltip.hover :title="data.item.error || 'Falló el envío'">
                  <AppIcon name="error" class="mr-1" />Error
                </b-badge>
              </template>

              <template v-slot:cell(delivery_status)="data">
                <b-badge v-if="data.item.delivery_status" :variant="deliveryStatusVariant(data.item.delivery_status)" v-b-tooltip.hover :title="data.item.error || ''">
                  <AppIcon :name="deliveryStatusIcon(data.item.delivery_status)" class="mr-1" />{{ deliveryStatusLabel(data.item.delivery_status) }}
                </b-badge>
                <span class="text-muted" v-else-if="data.item.channel == 'whatsapp' && data.item.status" v-b-tooltip.hover title="Todavía no llegó la confirmación de entrega de Meta">
                  <AppIcon name="schedule" class="mr-1" />Pendiente
                </span>
                <span class="text-muted" v-else>-</span>
              </template>

        </b-table>
        <EmptyState v-else
                    icon="notifications"
                    title="No se encontraron notificaciones"
                    description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda." />

        <div class="app-table-footer" v-if="table.items.length">
          <span class="list-view-toolbar__count">{{ table.items.length }} notificaciones</span>
        </div>
      </b-card>

      <b-sidebar
          id="sidebar-notifications-log-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de la notificación"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">
                <AppIcon :name="channelIcon(itemSelected.channel)" class="mr-1" />{{ channelLabel(itemSelected.channel) }}
              </span>
              <div>{{ itemSelected.module ? moduleLabel(itemSelected.module) : 'Notificación del sistema' }}</div>
              <div class="list-view-drawer__meta" v-b-tooltip.hover :title="formatDate(itemSelected.date_time)">
                {{ capitalize(formatRelative(itemSelected.date_time)) }}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoPrev"
                        v-b-tooltip.hover title="Notificación anterior" @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoNext"
                        v-b-tooltip.hover title="Notificación siguiente" @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Resultado</span>
              <span class="list-view-drawer__stat-value">
                <AppIcon :name="itemSelected.status ? 'task_alt' : 'error'" class="mr-1" />{{ itemSelected.status ? 'Enviado' : 'Error' }}
              </span>
            </div>
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Entrega</span>
              <span class="list-view-drawer__stat-value">{{ deliveryDetailLabel(itemSelected) }}</span>
            </div>
          </div>

          <div>
            <div class="notifications-log-drawer-channel-card">
              <span class="notifications-log-drawer-channel-card__icon">
                <AppIcon :name="channelIcon(itemSelected.channel)" />
              </span>
              <span class="notifications-log-drawer-channel-card__content">
                <span class="notifications-log-drawer-channel-card__label">Canal de envío</span>
                <strong>{{ channelLabel(itemSelected.channel) }}</strong>
              </span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="person" /> Destinatario</div>
            <div class="notifications-log-drawer-value">{{ itemSelected.to || 'Sin destinatario registrado' }}</div>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="account_tree" /> Origen</div>
            <div class="notifications-log-drawer-value">
              {{ itemSelected.module ? moduleLabel(itemSelected.module) : 'Sistema' }}
              <span v-if="itemSelected.reference" class="notifications-log-drawer-reference">{{ itemSelected.reference }}</span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="chat" /> Mensaje</div>
            <div class="notifications-log-drawer-message" v-if="itemSelected.description">
              <strong class="notifications-log-drawer-message__title" v-if="messageTitle(itemSelected)">{{ messageTitle(itemSelected) }}</strong>
              <span>{{ messageDetail(itemSelected) }}</span>
            </div>
            <div class="notifications-log-drawer-message" v-else>Sin contenido registrado</div>
          </div>

          <div v-if="itemSelected.error">
            <div class="list-view-drawer__section-title"><AppIcon name="error" /> Detalle del error</div>
            <div class="notifications-log-drawer-error">{{ itemSelected.error }}</div>
          </div>
        </div>
      </b-sidebar>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" /> Volver
          </b-button>

          <nav v-if="table.items.length">
            <b-pagination class="mb-0"
                          size="sm"
                          pills=""
                          :total-rows="table.items.length"
                          :per-page="table.perPage"
                          v-model="table.currentPage" />
          </nav>
        </div>
      </b-card>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Storage from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import moment from 'moment'

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.NOTIFICACIONES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'log',
          elements: {}
        },
        table: {
          items: [],
          currentPage: 1,
          perPage: 50,
          fields: [
            {key: 'date_time', label: 'Fecha', class: "align-middle text-left", width: "145px"},
            {key: 'channel', label: 'Canal', class: "align-middle text-left", width: "135px"},
            {key: 'process', label: 'Proceso', class: "align-middle text-left", width: "200px"},
            {key: 'to', label: 'Destinatario', class: "align-middle text-left", width: "220px"},
            {key: 'description', label: 'Mensaje', class: "align-middle text-left"},
            {key: 'status', label: 'Estado', class: "align-middle text-left", width: "125px"},
            {key: 'delivery_status', label: 'Entrega', class: "align-middle text-left", width: "140px"},
          ],
        },
        filter: {
          date_start: '',
          date_end: '',
          channel: '',
          module_id: '',
          status: '',
          reference: '',
        },
        filtersOpen: false,
        isMobileViewport: false,
        itemSelected: null,
        detailDrawer: false,
        arr: {
          select: {
            channels: [
              { value: '', text: 'TODOS' },
              { value: 'internal', text: 'Interna' },
              { value: 'email', text: 'Email' },
              { value: 'sms', text: 'SMS' },
              { value: 'whatsapp', text: 'WhatsApp' },
              { value: 'push', text: 'Push' },
            ],
            status: [
              { value: '', text: 'TODOS' },
              { value: '1', text: 'Éxito' },
              { value: '0', text: 'Error' },
            ],
          }
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */

      this.restoreFilterStorage()

      if (this.$route.query.module_id || this.$route.query.reference) {
        this.filter.module_id = this.$route.query.module_id || ''
        this.filter.reference = this.$route.query.reference || ''
      }

      if (!this.filter.date_start && !this.filter.date_end) {
        this.filter.date_end = moment().format('YYYY-MM-DD')
        this.filter.date_start = moment().subtract(7, 'days').format('YYYY-MM-DD')
      }
    },
    mounted() {
      this.filtersOpen = Storage.getValue('filters_open_notifications_log') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.search()

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      if (this._start) {
        this._start = false
        return
      }

      this.search()
    },
    computed: {
      moduleOptions() {
        var seen = {}
        var options = [{ value: '', text: 'TODOS' }]

        this.table.items.forEach(item => {
          if (item.module && !seen[item.module.id]) {
            seen[item.module.id] = true
            options.push({ value: item.module.id, text: this.moduleLabel(item.module) })
          }
        });

        return options
      },
      currentIndex() {
        return this.itemSelected ? this.table.items.indexOf(this.itemSelected) : -1
      },
      canGoPrev() {
        return this.currentIndex > 0
      },
      canGoNext() {
        return this.currentIndex >= 0 && this.currentIndex < this.table.items.length - 1
      },
    },
    watch: {
      filtersOpen(value) {
        Storage.setValue('filters_open_notifications_log', value ? 'true' : 'false')
      },
    },
    methods: {
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && item === this.itemSelected) return 'app-table-row-active'
        return ''
      },
      openSub(item) {
        if(!item) return
        this.itemSelected = item
        this.detailDrawer = true
      },
      hideSub() {
        this.itemSelected = null
        this.detailDrawer = false
      },
      goPrev() {
        if(this.canGoPrev) this.openSub(this.table.items[this.currentIndex - 1])
      },
      goNext() {
        if(this.canGoNext) this.openSub(this.table.items[this.currentIndex + 1])
      },
      search() {
        let loader = this.$loading.show()

        this.saveFilterStorage()
        this.table.currentPage = 1

        serviceAPI.listNotificationsLog(this.filter)
          .then((response) => {
            loader.hide()
            this.table.items = response.data || []
            this.hideSub()
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      moduleLabel(module) {
        var label = module.title || module.name
        return label.substring(label.indexOf('.') + 1)
      },
      channelLabel(channel) {
        var labels = { internal: 'Interna', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push' }
        return labels[channel] || channel
      },
      channelVariant(channel) {
        var variants = { internal: 'info', email: 'primary', sms: 'secondary', whatsapp: 'success', push: 'dark' }
        return variants[channel] || 'secondary'
      },
      channelIcon(channel) {
        var icons = { internal: 'notifications', email: 'mail', sms: 'sms', whatsapp: 'forum', push: 'notifications' }
        return icons[channel] || 'notifications'
      },
      deliveryStatusLabel(status) {
        var labels = { sent: 'Enviado', delivered: 'Entregado', read: 'Leído', failed: 'Falló' }
        return labels[status] || status
      },
      deliveryStatusVariant(status) {
        var variants = { sent: 'secondary', delivered: 'info', read: 'success', failed: 'danger' }
        return variants[status] || 'secondary'
      },
      deliveryStatusIcon(status) {
        var icons = { sent: 'send', delivered: 'done_all', read: 'mark_email_read', failed: 'error' }
        return icons[status] || 'schedule'
      },
      deliveryDetailLabel(item) {
        if(item.delivery_status) return this.deliveryStatusLabel(item.delivery_status)
        if(item.channel == 'whatsapp' && item.status) return 'Pendiente de confirmación'
        return 'Sin seguimiento'
      },
      messageTitle(item) {
        if(item.source !== 'internal' || !item.description) return ''
        var separator = item.description.indexOf('\n')
        return separator === -1 ? '' : item.description.substring(0, separator).trim()
      },
      messageDetail(item) {
        var title = this.messageTitle(item)
        if(!title) return item.description
        return item.description.substring(item.description.indexOf('\n') + 1).trim()
      },
      clearReferenceFilter() {
        this.filter.reference = ''
        this.search()
      },
      formatDate(date) {
        return moment(date).format('DD/MM/YYYY HH:mm')
      },
      formatRelative(date) {
        return moment(date).fromNow()
      },
      capitalize(text) {
        if(!text) return ''
        return text.charAt(0).toUpperCase() + text.slice(1)
      },
      saveFilterStorage() {
        Storage.setValue('filter_view_notifications_log', JSON.stringify(this.filter))
      },
      restoreFilterStorage() {
        if (Storage.getValue('filter_view_notifications_log')) {
          Object.assign(this.filter, JSON.parse(Storage.getValue('filter_view_notifications_log')))
        }
      },
    }
  }
</script>
<style scoped>
.notifications-log-reference-alert {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}
.notifications-log-date,
.notifications-log-recipient {
  display: inline-flex;
  align-items: center;
}
.notifications-log-date {
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-sm);
}
.notifications-log-recipient {
  max-width: 100%;
}
.notifications-log-recipient span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notifications-log-process {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.notifications-log-process__reference,
.notifications-log-drawer-reference {
  color: var(--app-color-text-muted);
  font-size: var(--app-text-xs);
}
.notifications-log-message {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 480px;
  color: var(--app-color-text-secondary);
  white-space: pre-line;
  word-break: break-word;
}
.notifications-log-message__title {
  display: block;
  color: var(--app-color-text);
  font-weight: var(--app-font-semibold);
}
@media (max-width: 767.98px) {
  .notifications-log-reference-alert {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
<style>
.notifications-log-drawer-value,
.notifications-log-drawer-message,
.notifications-log-drawer-error {
  color: var(--app-color-text);
  font-size: var(--app-text-sm);
  word-break: break-word;
}
.notifications-log-drawer-message,
.notifications-log-drawer-error {
  padding: 10px 12px;
  border: 1px solid var(--app-color-border);
  border-radius: 9px;
  background: var(--app-color-surface-subtle);
  white-space: pre-line;
}
.notifications-log-drawer-message__title {
  display: block;
  margin-bottom: 6px;
  color: var(--app-color-text);
  font-weight: var(--app-font-semibold);
}
.notifications-log-drawer-error {
  border-color: var(--app-color-danger-border);
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger-hover);
}
.notifications-log-drawer-reference {
  display: block;
  margin-top: 2px;
}
.notifications-log-drawer-channel-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--app-color-border);
  border-radius: 9px;
  background: var(--app-color-surface-subtle);
}
.notifications-log-drawer-channel-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.notifications-log-drawer-channel-card__content {
  display: flex;
  flex-direction: column;
  gap: 1px;
  color: var(--app-color-text);
  font-size: var(--app-text-sm);
}
.notifications-log-drawer-channel-card__label {
  color: var(--app-color-text-muted);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-medium);
}
</style>
